import { isNonNullable } from 'lib/array/arrayUtils'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { IntlShape } from 'react-intl'

function buildPriceElements(
  intl: IntlShape,
  prices: Array<App.CruisePassengerPricing> | Array<App.Cruises.BookingCabinPassengerPricing>,
  typeKey: string,
  typeLabel: string,
): Array<App.Checkout.ItemBreakdownElement> {
  return prices.map((pricing: App.CruisePassengerPricing | App.Cruises.BookingCabinPassengerPricing, index) => {
    const { total } = pricing
    const { amount } = total
    return {
      id: `passenger-${typeKey}-${index}`,
      left: {
        type: 'text',
        value: intl.formatMessage(
          { id: 'z7r+N8EQkc', defaultMessage: '1 {passengerType}', description: 'Single passenger pricing label in cruise cabin summary breakdown' },
          { passengerType: typeLabel },
        ),
      },
      right: {
        type: 'price',
        value: amount,
      },
    } as App.Checkout.ItemBreakdownElement
  })
}

function buildCabinCategoryDetails(itemView: App.Checkout.CruiseAccommodationItemView): string {
  const [categoryInfo] = itemView?.cabinCategory?.cabinCategoryInfo || []
  const categoryDetails: Array<string> = []

  if (categoryInfo?.name) {
    categoryDetails.push(categoryInfo.name)
  }

  if (itemView.item.cabinCode) {
    categoryDetails.push(itemView.item.cabinCode)
  }

  if (itemView.item.cabinNumber) {
    categoryDetails.push(itemView.item.cabinNumber)
  }

  return categoryDetails.join(' · ')
}

export function buildCruiseSummaryBreakdown(
  intl: IntlShape,
  itemView: App.Checkout.CruiseAccommodationItemView,
) {
  const rateCodeDescription = itemView?.cabinRate?.rateCodeDescription
  let additionalElements: Array<App.Checkout.ItemBreakdownElement> = []

  if (itemView.cabinSelection) {
    const { cabinPricingData, cabinSelection } = itemView
    const numAdults = itemView.occupancy.adults
    const numChildren = itemView.occupancy.children ?? 0
    const numInfants = itemView.occupancy.infants ?? 0

    const passengerPricing = cabinPricingData?.cabinPricing?.pricing.passengerPricing
    const pricingList = passengerPricing || cabinSelection?.pricing?.passengerPricing || []

    const adultPrices = pricingList.slice(0, numAdults)
    const childPrices = pricingList.slice(numAdults, numAdults + numChildren)
    const infantPrices = pricingList.slice(numAdults + numChildren, numAdults + numChildren + numInfants)

    const adultLabel = intl.formatMessage({
      id: 'em4920mBVs',
      defaultMessage: 'Adult',
      description: 'Passenger type label for adults in cruise cabin summary',
    })
    const childLabel = intl.formatMessage({
      id: 'Wlf4e0uG/7',
      defaultMessage: 'Child',
      description: 'Passenger type label for children in cruise cabin summary',
    })
    const infantLabel = intl.formatMessage({
      id: 'aMiFVKzzCJ',
      defaultMessage: 'Infant',
      description: 'Passenger type label for infants in cruise cabin summary',
    })

    additionalElements = [
      ...buildPriceElements(intl, adultPrices, 'Adult', adultLabel),
      ...buildPriceElements(intl, childPrices, 'Child', childLabel),
      ...buildPriceElements(intl, infantPrices, 'Infant', infantLabel),
    ]
  }

  const additionalInfo: Array<App.Checkout.ItemBreakdownViewAdditionalText> = [
    { text: buildCabinCategoryDetails(itemView) },
    rateCodeDescription ? {
      text: intl.formatMessage(
        { id: 'lYQu/TsQSL', defaultMessage: 'Rate: {rateCodeDescription}', description: 'Rate code description label in cruise cabin summary' },
        { rateCodeDescription },
      ),
    } : undefined,
    !itemView.cabinSelection ? { text: formatOccupantsShort(itemView.occupancy) } : undefined,
  ].filter(isNonNullable)

  return {
    additionalElements,
    additionalInfo,
  }
}
